(() => {
  'use strict';
  const store = window.GasNeedsQuote;
  if (!store) return;
  const normalise = (items) => items.map((item) => ({
    key: String(item.key || item.id), id: Number(item.id || item.variant_id),
    title: String(item.product_title || item.title || ''),
    variant: String(item.variant_title || item.variant || '').replace(/^Default Title$/, ''),
    sku: String(item.sku || ''), quantity: Number(item.quantity), url: String(item.url || ''),
    image: String(item.featured_image?.url || item.featured_image?.src || item.image?.src || item.image || ''),
    quantity_rule: item.quantity_rule,
    properties: Object.fromEntries(Object.entries(Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties || {})
      .filter(([key, value]) => !key.startsWith('_') && value !== '' && value !== null).sort(([a], [b]) => a.localeCompare(b)))
  }));
  const signature = (items) => JSON.stringify(normalise(items).map(({image, quantity_rule, ...item}) => item));
  const sameMerchandise = (a, b) => a.id === b.id && JSON.stringify(a.properties) === JSON.stringify(b.properties);

  async function fetchJSON(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal});
      const body = await response.json();
      if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : typeof body.message === 'string' ? body.message : 'The request could not be completed. Please try again.');
      return body;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('Connection interrupted. Check your connection and try again.');
      throw error;
    } finally {clearTimeout(timeout);}
  }
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function initialise(root) {
    if (root.dataset.gnReady) return;
    root.dataset.gnReady = 'true';
    const contact = root.querySelector('[data-gn-contact]');
    const submitButton = contact.querySelector('[data-gn-submit]');
    const productsField = contact.querySelector('[data-gn-products]');
    const notes = contact.querySelector('[data-gn-notes]');
    const formError = contact.querySelector('[data-gn-form-error]');
    const basketStatus = root.querySelector('[data-gn-basket-status]');
    const nativeList = root.querySelector('[data-gn-native-list]');
    const inquiryList = root.querySelector('[data-gn-inquiry-list]');
    let items = normalise(JSON.parse(root.querySelector('[data-gn-cart]').textContent));
    const pending = new Map();
    const drafts = new Map();
    const versions = new Map();
    const inquiryDrafts = new Map();
    let saveTimer, saving = null, lastSaveError = '', verifying = false, allowNativeSubmission = false, inquiryLoading = false, navigating = false;
    let inquiryTask = Promise.resolve();
    let quantitiesLocked = false;
    let renderedInquiries = [];

    const setError = (message) => {formError.textContent = message; formError.hidden = !message;};
    const setStatus = (message, error = false) => {basketStatus.textContent = message; basketStatus.dataset.error = String(error);};
    const itemUrl = (item, inquiry) => inquiry ? store.productUrl(item) : new URL(item.url, root.dataset.shopUrl).pathname + new URL(item.url, root.dataset.shopUrl).search;
    const allQuantityInputs = () => [...root.querySelectorAll('[data-gn-key], [data-gn-inquiry-quantity]')];
    const setLocked = (locked) => {
      quantitiesLocked = locked;
      allQuantityInputs().forEach((input) => {input.disabled = locked;});
      root.querySelectorAll('[data-gn-remove], [data-gn-remove-inquiry]').forEach((button) => {button.setAttribute('aria-disabled', String(locked));});
    };
    function updateSummary() {
      const enquiries = store.getItems();
      const requested = [...items, ...enquiries.map((item) => ({...item, url: store.productUrl(item), inquiry: true}))];
      productsField.value = requested.length ? requested.map((item, index) => {
        const lines = [`${index + 1}. ${item.title}`];
        if (item.variant) lines.push(`Variant: ${item.variant}`);
        if (item.sku) lines.push(`SKU: ${item.sku}`);
        lines.push(`Quantity: ${item.quantity}`, `Product: ${new URL(item.url, root.dataset.shopUrl).href}`);
        Object.entries(item.properties || {}).forEach(([key, value]) => lines.push(`${key}: ${value}`));
        if (item.inquiry) lines.push('Availability to confirm.');
        return lines.join('\n');
      }).join('\n\n') : 'No cart products selected. See request details.';
      notes.required = requested.length === 0;
      root.querySelector('[data-gn-notes-label]').textContent = notes.required ? '(required without a product)' : '(optional)';
      root.querySelector('[data-gn-empty]').hidden = requested.length > 0;
      const count = requested.reduce((sum, item) => sum + item.quantity, 0);
      root.querySelector('[data-gn-total-count]').textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      store.setNativeCount(items.reduce((sum, item) => sum + item.quantity, 0));
    }
    function makeRow(item, inquiry) {
      const row = element('li', 'gn-quote__item');
      row.dataset[inquiry ? 'gnInquiryId' : 'gnLineKey'] = String(inquiry ? item.id : item.key);
      const picture = element('a', 'gn-quote__image'); picture.tabIndex = -1; picture.setAttribute('aria-hidden', 'true');
      const info = element('div', 'gn-quote__item-info');
      const quantityBox = element('div', 'gn-quote__quantity');
      const label = element('label', '', 'Quantity');
      const input = element('input'); Object.assign(input, {type: 'number', inputMode: 'numeric', min: '1', max: '10000', step: '1', required: true});
      if (inquiry) input.dataset.gnInquiryQuantity = String(item.id); else input.dataset.gnKey = item.key;
      input.id = `GNQty-${root.dataset.sectionId}-${inquiry ? 'enquiry' : 'cart'}-${item.id}-${Math.random().toString(36).slice(2,7)}`;
      label.htmlFor = input.id; quantityBox.append(label, input); row.append(picture, info, quantityBox);
      return row;
    }
    function paintRow(row, item, inquiry) {
      const url = itemUrl(item, inquiry);
      const picture = row.querySelector('.gn-quote__image'); picture.href = url;
      if (picture.dataset.gnImage !== item.image) {
        picture.replaceChildren();
        if (item.image) {const img = element('img'); img.src = item.image; img.alt = ''; img.loading = 'lazy'; picture.append(img);}
        else picture.append(element('span', 'gn-quote__placeholder', 'GASNEEDS'));
        picture.dataset.gnImage = item.image || '';
      }
      const info = row.querySelector('.gn-quote__item-info'); info.replaceChildren();
      const title = element('a', 'gn-quote__item-title', item.title); title.href = url; info.append(title);
      if (item.variant) info.append(element('p', '', item.variant));
      if (item.sku) info.append(element('p', '', `SKU: ${item.sku}`));
      Object.entries(item.properties || {}).forEach(([key, value]) => info.append(element('p', '', `${key}: ${value}`)));
      if (inquiry) info.append(element('p', 'gn-quote__availability-label', 'Availability to confirm'));
      const remove = element('button', 'gn-quote__remove', 'Remove'); remove.type = 'button'; remove.setAttribute('aria-label', `Remove ${item.title}`);
      remove.dataset[inquiry ? 'gnRemoveInquiry' : 'gnRemove'] = String(inquiry ? item.id : item.key); info.append(remove);
      const input = row.querySelector('input'); input.setAttribute('aria-label', `Quantity for ${item.title}`);
      if (!inquiry) {
        input.dataset.gnKey = item.key; input.name = `updates[${item.key}]`;
        if (item.quantity_rule) {input.min = String(item.quantity_rule.min || 1); input.step = String(item.quantity_rule.increment || 1);}
input.max = '10000';
      }
      input.dataset.original = String(item.quantity);
      const draft = inquiry ? inquiryDrafts.get(item.id) : drafts.get(item.key)?.value;
      input.value = draft === undefined ? String(item.quantity) : draft;
      input.disabled = quantitiesLocked;
    }
    function renderNative() {
      const existing = new Map([...nativeList.children].map((row) => [row.dataset.gnLineKey, row]));
      items.forEach((item) => {
        const row = existing.get(item.key) || makeRow(item, false); existing.delete(item.key);
        paintRow(row, item, false); if (!row.isConnected) nativeList.append(row);
      });
      existing.forEach((row, key) => {row.remove(); drafts.delete(key); pending.delete(key);});
      updateSummary();
    }
    function renderInquiries() {
      renderedInquiries = store.getItems();
      const existing = new Map([...inquiryList.children].map((row) => [Number(row.dataset.gnInquiryId), row]));
      renderedInquiries.forEach((item) => {
        const row = existing.get(item.id) || makeRow(item, true); existing.delete(item.id);
        paintRow(row, item, true); if (!row.isConnected) inquiryList.append(row);
      });
      existing.forEach((row, id) => {row.remove(); inquiryDrafts.delete(id);});
      inquiryList.hidden = renderedInquiries.length === 0;
      updateSummary();
    }
    function adoptCart(cart) {
      if (!Array.isArray(cart.items)) throw new Error('Could not confirm your cart. Please reload this page.');
      const next = normalise(cart.items);
      const remapped = new Map();
      items.forEach((old) => {
        const matches = next.filter((item) => sameMerchandise(old, item));
        if (matches.length !== 1) return;
        const item = matches[0];
        item.image ||= old.image; item.quantity_rule ||= old.quantity_rule;
        if (item.key === old.key) return;
        remapped.set(old.key, item.key);
        [drafts, pending, versions].forEach((map) => {if (map.has(old.key)) {map.set(item.key, map.get(old.key)); map.delete(old.key);}});
        const row = [...nativeList.children].find((entry) => entry.dataset.gnLineKey === old.key);
        if (row) {row.dataset.gnLineKey = item.key; row.querySelector('[data-gn-key]').dataset.gnKey = item.key;}
      });
      items = next;
      return remapped;
    }
    function validQuantity(input) {
  input.setCustomValidity('');
  const quantity = Number(input.value);
  if (input.value && (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 10000)) input.setCustomValidity('Enter a whole quantity from 1 to 10,000.');
  return input.checkValidity();
}
    function queueInput(input) {
      const key = input.dataset.gnKey;
      const version = (versions.get(key) || 0) + 1; versions.set(key, version);
      drafts.set(key, {value: input.value, version});
      if (validQuantity(input)) {
        if (input.value === input.dataset.original && !saving) {drafts.delete(key); pending.delete(key);}
        else pending.set(key, {quantity: Number(input.value), version});
      } else pending.delete(key);
      clearTimeout(saveTimer);
      if (pending.size) {setStatus('Saving quantities…'); saveTimer = setTimeout(drain, 600);}
    }
    function drain() {
      clearTimeout(saveTimer);
      if (saving) return saving;
      if (!pending.size) return Promise.resolve();
      lastSaveError = '';
      saving = (async () => {
        root.querySelector('.gn-quote__basket').setAttribute('aria-busy', 'true');
        try {
          while (pending.size) {
            const [key, change] = pending.entries().next().value; pending.delete(key);
            let effectiveKey = key;
            try {
              const cart = await fetchJSON(`${root.dataset.changeUrl}.js`, {method: 'POST', keepalive: true, headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify({id: key, quantity: change.quantity})});
              effectiveKey = adoptCart(cart).get(key) || key;
              if (drafts.get(effectiveKey)?.version === change.version) drafts.delete(effectiveKey);
            } catch (error) {
              lastSaveError = error.message;
              try {
                const cart = await fetchJSON(`${root.dataset.cartUrl}.js`);
                effectiveKey = adoptCart(cart).get(key) || key;
                if (drafts.get(effectiveKey)?.version === change.version) drafts.delete(effectiveKey);
              } catch (_) {lastSaveError += ' Your latest quantity has not been confirmed.';}
            }
            renderNative();
          }
          setStatus(lastSaveError ? `${lastSaveError} Please review the quantities shown.` : 'Quantities saved.', Boolean(lastSaveError));
        } finally {root.querySelector('.gn-quote__basket').setAttribute('aria-busy', 'false');}
      })().catch((error) => {lastSaveError = error.message; setStatus(error.message, true);}).finally(() => {saving = null;});
      return saving;
    }
    function saveInquiryInput(input) {
      const id = Number(input.dataset.gnInquiryQuantity);
      inquiryDrafts.set(id, input.value);
      if (!validQuantity(input)) return;
      try {store.setQuantity(id, Number(input.value)); inquiryDrafts.delete(id); input.dataset.original = input.value; setStatus('Quantities saved.');}
      catch (error) {lastSaveError = error.message; setStatus(error.message, true);}
    }
    async function flush() {
      clearTimeout(saveTimer);
      for (const input of allQuantityInputs()) {
        if (!validQuantity(input)) {input.reportValidity(); input.focus(); return false;}
        if (input.dataset.gnInquiryQuantity && input.value !== input.dataset.original) saveInquiryInput(input);
        if (input.dataset.gnKey && input.value !== input.dataset.original && !pending.has(input.dataset.gnKey) && !saving) queueInput(input);
      }
      await drain();
      if (pending.size) await drain();
      if (lastSaveError) {setError(`${lastSaveError} Review the quantities before continuing.`); lastSaveError = ''; return false;}
      if (drafts.size || inquiryDrafts.size) {setError('A quantity could not be saved. Check the highlighted fields and try again.'); return false;}
      return true;
    }
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-gn-key]')) queueInput(event.target);
      if (event.target.matches('[data-gn-inquiry-quantity]')) saveInquiryInput(event.target);
    });
    root.addEventListener('change', (event) => {if (event.target.matches('[data-gn-key]')) drain();});
    root.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-gn-update-form]')) return;
      event.preventDefault(); flush();
    });
    root.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-gn-remove], [data-gn-remove-inquiry]');
      if (!remove || !root.contains(remove)) return;
      event.preventDefault();
      if (verifying) return;
      if (remove.dataset.gnRemoveInquiry) {
        try {store.remove(Number(remove.dataset.gnRemoveInquiry)); setStatus('Product removed.');}
        catch (error) {setStatus(error.message, true);}
      } else {
        const key = remove.dataset.gnRemove;
        const version = (versions.get(key) || 0) + 1; versions.set(key, version); drafts.delete(key);
        pending.set(key, {quantity: 0, version}); drain();
      }
    });

    function validateContact() {
      const name = contact.querySelector('[data-gn-name]');
      const phone = contact.querySelector('[data-gn-phone]');
      const email = contact.querySelector('[name="contact[email]"]');
      const nameValue = name.value.trim();
      name.setCustomValidity(/\p{L}/u.test(nameValue) && /^[\p{L}\p{M} .'’\-]+$/u.test(nameValue) ? '' : 'Enter your name using letters, spaces, apostrophes or hyphens. Numbers are not allowed.');
      const phoneValue = phone.value.trim(); const digits = phoneValue.replace(/\D/g, '');
      phone.setCustomValidity(phoneValue && /^\+?[\d\s().-]+$/.test(phoneValue) && digits.length >= 7 && digits.length <= 15 ? '' : 'Enter a phone number with 7–15 digits, for example +971 50 123 4567.');
      email.setCustomValidity(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()) ? '' : 'Enter a complete email address, for example name@company.com.');
      notes.setCustomValidity(notes.required && !notes.value.trim() ? 'Describe the products or services you need.' : '');
    }
    contact.addEventListener('input', () => {validateContact(); setError('');});
    submitButton.addEventListener('click', validateContact);
    contact.addEventListener('submit', async (event) => {
      if (allowNativeSubmission) return;
      event.preventDefault();
      if (verifying || navigating) return;
      validateContact(); if (!contact.reportValidity()) return;
      verifying = true; setError('');
      const originalLabel = submitButton.textContent;
      submitButton.disabled = true; submitButton.textContent = 'Checking your request…';
      try {
        await inquiryTask;
        if (!await flush()) return;
        setLocked(true);
        const inquirySnapshot = JSON.stringify(store.getItems());
        if (store.getError()) throw new Error(store.getError());
        const cart = await fetchJSON(`${root.dataset.cartUrl}.js`);
        if (!Array.isArray(cart.items)) throw new Error('Could not confirm your cart. Please reload this page.');
        if (signature(items) !== signature(cart.items) || inquirySnapshot !== JSON.stringify(store.getItems())) {
          adoptCart(cart); renderNative(); renderInquiries();
          setError('Your cart changed. Review the products and quantities, then submit again.'); return;
        }
        updateSummary(); validateContact();
        if (!contact.reportValidity()) return;
        allowNativeSubmission = true; submitButton.disabled = false; submitButton.textContent = originalLabel;
        contact.requestSubmit(event.submitter || submitButton);
        setTimeout(() => {allowNativeSubmission = false;}, 0);
      } catch (error) {setError(`${error.message} Your request has not been submitted.`);}
      finally {verifying = false; setLocked(false); submitButton.disabled = false; submitButton.textContent = originalLabel;}
    });

    async function loadInquiry() {
      const url = new URL(window.location.href);
      const handle = url.searchParams.get('gn_product');
      if (!handle) return;
      inquiryLoading = true; setStatus('Adding your product…');
      try {
        if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) throw new Error('The product link is invalid. Please select the product again.');
        const product = await fetchJSON(`${root.dataset.rootUrl}products/${encodeURIComponent(handle)}.js`);
        const variant = product.variants?.find((entry) => String(entry.id) === url.searchParams.get('gn_variant'));
        if (!variant) throw new Error('The selected product option could not be found. Please select it again.');
        const quantity = Number(url.searchParams.get('gn_quantity') || 1);
        if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 10000) throw new Error('Enter a whole quantity from 1 to 10,000.');
        const added = store.add({id: variant.id, handle, title: product.title, variant: variant.title, sku: variant.sku || '', quantity, image: variant.featured_image?.src || product.featured_image || ''});
        ['gn_product','gn_variant','gn_quantity'].forEach((key) => url.searchParams.delete(key));
        window.history.replaceState(window.history.state, '', url.href);
        renderInquiries(); setStatus(added ? 'Product added to your cart.' : 'This product is already in your cart. You can change its quantity below.');
      } catch (error) {setError(error.message); setStatus(error.message, true);}
      finally {inquiryLoading = false;}
    }
    // Finish quantity saves before following a normal link out of the basket.
    document.addEventListener('click', async (event) => {
      const link = event.target.closest('a[href]');
      if (!root.isConnected || !link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download') || link.hasAttribute('data-action')) return;
      const destination = new URL(link.href, window.location.href);
      if (!['http:','https:'].includes(destination.protocol) || destination.origin !== window.location.origin || (destination.pathname === window.location.pathname && destination.search === window.location.search && destination.hash)) return;
      if (!drafts.size && !inquiryDrafts.size && !saving && !pending.size && !inquiryLoading) return;
      event.preventDefault();
      if (navigating || verifying) return;
      navigating = true;
      try {await inquiryTask; if (await flush()) window.location.assign(destination.href);}
      finally {navigating = false;}
    });
    document.addEventListener('gn:inquiries-changed', (event) => {
      if (!root.isConnected) return;
      renderInquiries();
      if (event.detail?.error) setStatus(event.detail.error, true);
      else if (event.detail?.external) setStatus('Your saved cart is up to date.');
    });
    document.addEventListener('gn:cart-changed', async (event) => {
      if (!root.isConnected || saving || verifying || drafts.size) return;
      try {adoptCart(event.detail?.cart || await fetchJSON(`${root.dataset.cartUrl}.js`)); renderNative();}
      catch (error) {setStatus(error.message, true);}
    });
    renderInquiries();
    root.querySelector('[data-gn-auto-note]').hidden = false;
    if (store.getError()) setStatus(store.getError(), true);
    inquiryTask = loadInquiry();
    const serverMessage = root.querySelector('[data-gn-success], [data-gn-server-error]');
    if (serverMessage) serverMessage.focus();
  }
  const boot = () => document.querySelectorAll('[data-gn-quote]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('cart:rerendered', boot, true);

  // Carry the quantity selected on a product page into an availability enquiry.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="gn_product="]');
    if (!link) return;
    const input = link.closest('.product-form')?.querySelector('[name="quantity"]');
    if (!input) return;
    if (!input.reportValidity()) {event.preventDefault(); return;}
    const url = new URL(link.href); url.searchParams.set('gn_quantity', input.value || '1'); link.href = url.href;
  });
  if (window.theme?.gnQuoteMode && ['collection', 'search'].includes(window.theme.pageType)) {
    const url = new URL(window.location.href); let changed = false;
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {if (url.searchParams.has(key)) {url.searchParams.delete(key); changed = true;}});
    if (['price-ascending', 'price-descending'].includes(url.searchParams.get('sort_by'))) {url.searchParams.delete('sort_by'); changed = true;}
    if (changed) {url.searchParams.delete('page'); window.location.replace(url.href);}
  }
})();
